import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, Col, Empty, InputNumber, Row, Select, Space, Spin, Statistic, Table, Tag, Tooltip } from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  DownloadOutlined,
  MinusCircleOutlined,
  CarOutlined,
  FieldTimeOutlined,
  InboxOutlined,
} from '@ant-design/icons';
import { Pie, Column } from '@ant-design/charts';
import StatsRoutesDrilldown from '../components/StatsRoutesDrilldown';
import { Link, useSearchParams } from 'react-router-dom';
import * as partnerApi from '../modules/partners/api';
import StatsFilters, { formatStatsRange } from '../components/StatsFilters';
import StatsByName from '../components/StatsByName';
import PartnerLogo from '../components/PartnerLogo';
import * as driversApi from '../modules/drivers/api';
import { downloadStatsCsv } from '../utils/statsCsvExport';
import { tenantConfig } from '../config/tenantConfig';
import './GlobalStats.scss';

// PERIOD_OPTIONS moved inside component to use translations

const STATUS_COLORS = {
  COMPLETED: '#2e7d32',
  DELIVERY_FAILED: '#c62828',
  SKIPPED: '#f57c00',
  ASSIGNED: '#1565c0',
  UNASSIGNED: '#9e9e9e',
  DISPATCHED: '#4527a0',
  COLLECTED: '#6a1b9a',
  PICKUP_ARRIVED: '#6a1b9a',
  DELIVERY_ARRIVED: '#6a1b9a',
  SERVICE_ARRIVED: '#6a1b9a',
  OPTIMIZED: '#00838f',
  ERROR: '#b71c1c',
  DRAFT: '#bdbdbd',
  CANCELLED: '#9c27b0',
};

const CATEGORY_COLORS = {
  PRODUCT: '#1565c0',
  EMPTY: '#f57c00',
  UNKNOWN: '#9e9e9e',
};

// OVO-247 — the billable span (first pickup → last delivery), actual badged
// times when the driver logged them. Scoped to the selected partners, so it
// equals the figure on that partner's own Stats page.
const CLIENT_SERVICE_HINT =
  'Billable time on site: first pickup → last delivery. With partners selected, ' +
  'only those partners’ stops are counted, so this matches their own Stats page. ' +
  'The driver’s full shift is shown separately as Shift.';

// OVO-248 — an aggregate you can't decompose is one you can't trust.
const DRILLDOWN_HINT = 'Click to see the routes that add up to this figure.';

const formatMins = (mins) => {
  if (!mins || mins <= 0) return '0m';
  const h = Math.floor(mins / 60);
  const m = Math.round(mins % 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

const completionRateTag = (val) => {
  const color = val >= 80 ? '#2e7d32' : val >= 50 ? '#f57c00' : '#c62828';
  return <Tag color={color}>{val}%</Tag>;
};

// Vehicle-type display: friendly i18n label + colour per FleetTag (Felipe — by
// vehicle type van vs bike). Falls back to the raw tag for any future type.
const VEHICLE_LABEL_KEY = { BIKE: 'vehicleBike', VAN: 'vehicleVan', UNKNOWN: 'vehicleUnknown' };
// OVO-221 — Vehicle Type filter options (multi-select). Bike/Van are the live
// FleetTags; labels reuse the i18n keys above.
const VEHICLE_TYPE_OPTIONS = ['BIKE', 'VAN'];

const GlobalStats = () => {
  const { t } = useTranslation('stats');

  // OVO-268 — the whole filter set lives in the URL so a shared link
  // reproduces the exact view (partner + vehicle + driver + period/range).
  const [searchParams, setSearchParams] = useSearchParams();
  const csvParam = (k) => (searchParams.get(k) || '').split(',').filter(Boolean);
  const csvNumParam = (k) => csvParam(k).map(Number).filter(Number.isFinite);

  const [filter, setFilter] = useState(() => ({
    period: searchParams.get('period') || 'this_week',
    from: searchParams.get('from') || undefined,
    to: searchParams.get('to') || undefined,
  }));
  // Per-client visit stats now consume the unified `byClient` block.
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [partnerOptions, setPartnerOptions] = useState([]);
  const [selectedPartnerIds, setSelectedPartnerIds] = useState(() => csvNumParam('partnerIds'));
  // OVO-221 — vehicle-type + driver filters (multi-select); scope the whole page.
  const [selectedVehicleTypes, setSelectedVehicleTypes] = useState(() =>
    csvParam('vehicleTypes').filter((v) => VEHICLE_TYPE_OPTIONS.includes(v)),
  );
  const [selectedDriverIds, setSelectedDriverIds] = useState(() => csvNumParam('driverIds'));
  const [driverOptions, setDriverOptions] = useState([]);
  // Editable CO₂ comparison baseline (g/km) — Felipe Jun 2026.
  const [baselineInput, setBaselineInput] = useState(null);
  const [savingBaseline, setSavingBaseline] = useState(false);
  const [baselineSaved, setBaselineSaved] = useState(false);
  // OVO-248 — which KPI's contributing routes are open, if any.
  const [drilldown, setDrilldown] = useState(null);

  // Load partner options for the filter
  useEffect(() => {
    const loadPartners = async () => {
      const { response } = await partnerApi.getPartners({ take: 200 });
      if (response?.partners) {
        setPartnerOptions(response.partners.map((p) => ({ label: p.name, value: p.id })));
      }
    };
    loadPartners();
  }, []);

  // OVO-221 — driver options for the Driver filter.
  useEffect(() => {
    const loadDrivers = async () => {
      const { response } = await driversApi.getDrivers({ take: 500 });
      const list = response?.drivers || response?.items || (Array.isArray(response) ? response : []);
      setDriverOptions((list || []).map((d) => ({ label: d.name, value: d.id })));
    };
    loadDrivers();
  }, []);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    const query = { period: filter.period, from: filter.from, to: filter.to };
    const { response } = await partnerApi.getGlobalStats(
      query,
      selectedPartnerIds,
      selectedVehicleTypes,
      selectedDriverIds,
    );
    if (response) setStats(response);
    setLoading(false);
  }, [filter.period, filter.from, filter.to, selectedPartnerIds, selectedVehicleTypes, selectedDriverIds]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // Mirror the filters back into the URL (replace — no history spam). The
  // default view keeps a clean /admin/stats.
  useEffect(() => {
    const p = {};
    if (filter.period && filter.period !== 'this_week') p.period = filter.period;
    if (filter.from) p.from = String(filter.from);
    if (filter.to) p.to = String(filter.to);
    if (selectedPartnerIds.length) p.partnerIds = selectedPartnerIds.join(',');
    if (selectedVehicleTypes.length) p.vehicleTypes = selectedVehicleTypes.join(',');
    if (selectedDriverIds.length) p.driverIds = selectedDriverIds.join(',');
    setSearchParams(p, { replace: true });
  }, [filter, selectedPartnerIds, selectedVehicleTypes, selectedDriverIds, setSearchParams]);

  // Seed the baseline editor from whatever the server currently applies.
  useEffect(() => {
    if (stats?.routes?.oilBaselineGPerKm != null) {
      setBaselineInput(stats.routes.oilBaselineGPerKm);
    }
  }, [stats?.routes?.oilBaselineGPerKm]);

  const handleSaveBaseline = async () => {
    if (!baselineInput || baselineInput <= 0) return;
    setSavingBaseline(true);
    const { error } = await partnerApi.setCo2Baseline(Number(baselineInput));
    setSavingBaseline(false);
    if (!error) {
      setBaselineSaved(true);
      setTimeout(() => setBaselineSaved(false), 2500);
      fetchStats(); // refetch so every CO₂ figure reflects the new baseline
    }
  };

  const handleExport = () => {
    if (!stats) return;
    const partnerLabel =
      selectedPartnerIds.length > 0
        ? partnerOptions
            .filter((p) => selectedPartnerIds.includes(p.value))
            .map((p) => p.label)
            .join(', ')
        : 'All partners';
    downloadStatsCsv(stats, {
      title: 'Global stats',
      partnerName: partnerLabel,
      filenameSlug: 'global-stats',
    });
  };

  // Partner comparison table columns
  const partnerColumns = [
    {
      title: t('partnerName'),
      dataIndex: 'partnerName',
      key: 'partnerName',
      sorter: (a, b) => a.partnerName.localeCompare(b.partnerName),
      render: (name, record) => (
        <Link
          to={`/admin/partners/${record.partnerId}/stats`}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
        >
          <PartnerLogo name={name} logoKey={record.partnerLogoKey} />
          <span>{name}</span>
        </Link>
      ),
    },
    {
      title: t('ordersColumn'),
      dataIndex: 'orders',
      key: 'orders',
      sorter: (a, b) => a.orders - b.orders,
      defaultSortOrder: 'descend',
    },
    {
      title: t('completedColumn'),
      dataIndex: 'completed',
      key: 'completed',
      sorter: (a, b) => a.completed - b.completed,
    },
    {
      title: t('completionRateColumn'),
      dataIndex: 'completionRate',
      key: 'completionRate',
      sorter: (a, b) => a.completionRate - b.completionRate,
      render: completionRateTag,
    },
    {
      title: t('routesColumn'),
      dataIndex: 'routes',
      key: 'routes',
      sorter: (a, b) => a.routes - b.routes,
    },
    {
      title: t('distanceKm'),
      dataIndex: 'totalDistanceKm',
      key: 'totalDistanceKm',
      sorter: (a, b) => a.totalDistanceKm - b.totalDistanceKm,
      render: (val) => val.toLocaleString(),
    },
    {
      title: t('itemsColumn'),
      dataIndex: 'items',
      key: 'items',
      sorter: (a, b) => a.items - b.items,
    },
  ];

  if (loading && !stats) {
    return (
      <div className="GlobalStatsPage Content">
        <Spin style={{ display: 'block', paddingTop: 80 }} />
      </div>
    );
  }

  // Chart data
  const statusData = stats
    ? Object.entries(stats.time.ordersByStatus).map(([status, value]) => ({
        type: status.replace(/_/g, ' '),
        value,
        color: STATUS_COLORS[status] || '#9e9e9e',
      }))
    : [];

  const onTimeData = stats
    ? [
        { type: 'On time', value: stats.time.onTimeCount, color: '#2e7d32' },
        { type: 'Late', value: stats.time.lateCount, color: '#c62828' },
      ].filter((d) => d.value > 0)
    : [];

  const categoryData = stats
    ? Object.entries(stats.collection.byCategory).map(([cat, value]) => ({
        type: cat,
        value,
        color: CATEGORY_COLORS[cat] || '#9e9e9e',
      }))
    : [];

  const dowOrder = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const dowData = stats ? dowOrder.map((day) => ({ day, orders: stats.time.ordersByDayOfWeek[day] || 0 })) : [];

  const pieConfig = {
    height: 240,
    innerRadius: 0.6,
    radius: 0.85,
    label: { text: 'value', style: { fontWeight: 600 } },
    legend: { color: { position: 'bottom', layout: { justifyContent: 'center' } } },
    tooltip: { title: 'type' },
    style: { lineWidth: 2, stroke: '#fff' },
  };

  // Echo the resolved range next to every section title + chart-card head
  // (same convention as PartnerStats — keeps the time window visible
  // wherever the operator scrolls).
  const rangeText = formatStatsRange(stats?.period);
  // OVO-239 — shared columns for the two comparison tables. Speed and
  // stops/hour are the metrics Felipe asked for; the tooltips state the
  // denominators, because a rate whose divisor is unclear is untrustworthy.
  const rateColumns = [
    {
      title: (
        <Tooltip title="Distance ÷ shift hours (bike-out → bike-in). Shifts longer than 18h are excluded as never-closed.">
          <span>{t('avgSpeed')}</span>
        </Tooltip>
      ),
      dataIndex: 'avgSpeedKmh',
      key: 'avgSpeedKmh',
      sorter: (a, b) => (a.avgSpeedKmh ?? 0) - (b.avgSpeedKmh ?? 0),
      render: (v) => (v == null ? '—' : `${v} km/h`),
    },
    {
      title: (
        <Tooltip title="Stops ÷ client-service hours (first pickup → last delivery), so depot time doesn't dilute it.">
          <span>{t('stopsPerHour')}</span>
        </Tooltip>
      ),
      dataIndex: 'stopsPerHour',
      key: 'stopsPerHour',
      sorter: (a, b) => (a.stopsPerHour ?? 0) - (b.stopsPerHour ?? 0),
      render: (v) => (v == null ? '—' : v),
    },
    {
      title: t('clientServiceTotal'),
      dataIndex: 'totalClientServiceMins',
      key: 'totalClientServiceMins',
      sorter: (a, b) => a.totalClientServiceMins - b.totalClientServiceMins,
      render: (v) => formatMins(v),
    },
    {
      title: t('ordersColumn'),
      dataIndex: 'orders',
      key: 'orders',
      sorter: (a, b) => a.orders - b.orders,
    },
    {
      title: t('completionRateColumn'),
      dataIndex: 'completionRate',
      key: 'completionRate',
      sorter: (a, b) => a.completionRate - b.completionRate,
      render: completionRateTag,
    },
    {
      title: t('distanceKm'),
      dataIndex: 'totalDistanceKm',
      key: 'totalDistanceKm',
      sorter: (a, b) => a.totalDistanceKm - b.totalDistanceKm,
      render: (v) => `${v} km`,
    },
    {
      title: t('routesColumn'),
      dataIndex: 'routes',
      key: 'routes',
      sorter: (a, b) => a.routes - b.routes,
    },
  ];

  const driverComparisonColumns = [
    {
      title: t('driverColumn'),
      dataIndex: 'driverName',
      key: 'driverName',
      sorter: (a, b) => (a.driverName || '').localeCompare(b.driverName || ''),
      render: (name, record) => <Link to={`/admin/drivers/${record.driverId}`}>{name}</Link>,
    },
    ...rateColumns,
    {
      // P1 Pause/Resume — informational: already excluded from shift +
      // client-service figures.
      title: (
        <Tooltip title="Driver pauses — excluded from worked hours and billable client service">
          <span>⏸ {t('pausedColumn', 'Paused')}</span>
        </Tooltip>
      ),
      dataIndex: 'totalPausedMins',
      key: 'totalPausedMins',
      sorter: (a, b) => (a.totalPausedMins ?? 0) - (b.totalPausedMins ?? 0),
      render: (v) => (v > 0 ? formatMins(v) : '—'),
    },
    {
      // Start punctuality — badged "Start route" vs planned shift start. A
      // late badge and a late start read the same: this is the "who isn't
      // using the app at the right moment" column (sort by it).
      title: (
        <Tooltip title={t('onTimeStartTooltip')}>
          <span>⏰ {t('onTimeStartColumn')}</span>
        </Tooltip>
      ),
      dataIndex: 'onTimeStartRate',
      key: 'onTimeStartRate',
      sorter: (a, b) => (a.onTimeStartRate ?? -1) - (b.onTimeStartRate ?? -1),
      render: (v, rec) =>
        v == null ? (
          '—'
        ) : (
          <span style={{ color: v >= 80 ? '#2e7d32' : v >= 50 ? '#f9a825' : '#c62828' }}>
            {v}%{rec.startNotBadged > 0 ? ` (+${rec.startNotBadged} ${t('notBadgedShort')})` : ''}
          </span>
        ),
    },
    {
      title: (
        <Tooltip title={t('avgStartDelayTooltip')}>
          <span>{t('avgStartDelayColumn')}</span>
        </Tooltip>
      ),
      dataIndex: 'avgStartDelayMins',
      key: 'avgStartDelayMins',
      sorter: (a, b) => (a.avgStartDelayMins ?? -9999) - (b.avgStartDelayMins ?? -9999),
      render: (v) =>
        v == null ? '—' : <span style={{ color: v > 10 ? '#c62828' : '#2e7d32' }}>{v > 0 ? `+${v}′` : `${v}′`}</span>,
    },
  ];

  const vehicleComparisonColumns = [
    {
      title: t('vehicleTypeColumn'),
      dataIndex: 'vehicleType',
      key: 'vehicleType',
      render: (tag) => t(VEHICLE_LABEL_KEY[tag] || 'vehicleUnknown'),
    },
    ...rateColumns,
  ];

  const sectionTitle = (label) => (
    <div className="section-title">
      <span>{label}</span>
      <span className="section-range">{rangeText}</span>
    </div>
  );
  const cardTitle = (label) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
      <span>{label}</span>
      <span className="section-range">{rangeText}</span>
    </div>
  );

  return (
    <div className="GlobalStatsPage Content">
      <div className="ContentHeader">
        <div className="HeaderTitle">{t('title')}</div>
      </div>

      <div className="partner-stats global-stats">
        <div className="global-stats-controls">
          <StatsFilters
            value={filter}
            onChange={setFilter}
            resolvedRange={stats?.period}
            rightSlot={
              <>
                {/* OVO-132 — partner comparison selector hidden in a single-tenant instance */}
                {!tenantConfig.isSingleTenant && (
                  <Select
                    mode="multiple"
                    placeholder={t('allPartners')}
                    value={selectedPartnerIds}
                    onChange={setSelectedPartnerIds}
                    options={partnerOptions}
                    style={{ minWidth: 280 }}
                    maxTagCount={3}
                    allowClear
                    showSearch
                    filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                  />
                )}
                {/* OVO-221 — Vehicle Type filter (Bike/Van) — scopes the whole page */}
                <Select
                  mode="multiple"
                  placeholder={t('allVehicleTypes')}
                  value={selectedVehicleTypes}
                  onChange={setSelectedVehicleTypes}
                  options={VEHICLE_TYPE_OPTIONS.map((v) => ({ label: t(VEHICLE_LABEL_KEY[v]), value: v }))}
                  style={{ minWidth: 170 }}
                  allowClear
                />
                {/* OVO-221 — Driver filter — scopes the whole page */}
                <Select
                  mode="multiple"
                  placeholder={t('allDrivers')}
                  value={selectedDriverIds}
                  onChange={setSelectedDriverIds}
                  options={driverOptions}
                  style={{ minWidth: 220 }}
                  maxTagCount={2}
                  allowClear
                  showSearch
                  filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                />
                <Button icon={<DownloadOutlined />} onClick={handleExport} disabled={!stats || loading}>
                  Export CSV
                </Button>
              </>
            }
          />
        </div>

        {!stats || stats.delivery.total === 0 ? (
          loading ? (
            <Spin style={{ display: 'block', paddingTop: 80 }} />
          ) : (
            <Empty description="No data for this period" style={{ paddingTop: 60 }} />
          )
        ) : (
          <Spin spinning={loading}>
            {/* Delivery Performance */}
            <div className="stats-section">
              {sectionTitle('Delivery Performance')}
              <Row gutter={[16, 16]}>
                <Col xs={12} sm={8} md={4}>
                  <Card className="stat-card">
                    <Statistic title="Total" value={stats.delivery.total} valueStyle={{ color: '#212121' }} />
                  </Card>
                </Col>
                <Col xs={12} sm={8} md={4}>
                  <Card className="stat-card success">
                    <Statistic
                      title="Completed"
                      value={stats.delivery.completed}
                      prefix={<CheckCircleOutlined />}
                      valueStyle={{ color: '#2e7d32' }}
                    />
                  </Card>
                </Col>
                <Col xs={12} sm={8} md={4}>
                  <Card className="stat-card danger">
                    <Statistic
                      title="Failed"
                      value={stats.delivery.failed}
                      prefix={<CloseCircleOutlined />}
                      valueStyle={{ color: '#c62828' }}
                    />
                  </Card>
                </Col>
                <Col xs={12} sm={8} md={4}>
                  <Card className="stat-card warning">
                    <Statistic
                      title="Skipped"
                      value={stats.delivery.skipped}
                      prefix={<MinusCircleOutlined />}
                      valueStyle={{ color: '#f57c00' }}
                    />
                  </Card>
                </Col>
                <Col xs={12} sm={8} md={4}>
                  <Card className="stat-card">
                    <Statistic title="Completion Rate" value={stats.delivery.completionRate} suffix="%" precision={1} />
                  </Card>
                </Col>
              </Row>
            </div>

            {/* Charts Row 1 */}
            <Row gutter={[16, 16]} className="stats-section">
              <Col xs={24} md={12}>
                <Card className="chart-card" title={cardTitle('Orders by Status')}>
                  {statusData.length > 0 ? (
                    <Pie
                      data={statusData}
                      angleField="value"
                      colorField="type"
                      color={statusData.map((d) => d.color)}
                      {...pieConfig}
                    />
                  ) : (
                    <Empty description="No data" />
                  )}
                </Card>
              </Col>
              <Col xs={24} md={12}>
                <Card className="chart-card" title={cardTitle('On-Time Performance')}>
                  {onTimeData.length > 0 ? (
                    <Pie
                      data={onTimeData}
                      angleField="value"
                      colorField="type"
                      color={['#2e7d32', '#c62828']}
                      {...pieConfig}
                    />
                  ) : (
                    <Empty description="No delivery time data" />
                  )}
                </Card>
              </Col>
            </Row>

            {/* Route Operations */}
            <div className="stats-section">
              {sectionTitle('Route Operations')}
              <Row gutter={[16, 16]}>
                <Col xs={12} sm={6}>
                  <Card className="stat-card">
                    <Statistic title="Routes" value={stats.routes.total} prefix={<CarOutlined />} />
                  </Card>
                </Col>
                <Col xs={12} sm={6}>
                  <Card className="stat-card">
                    <Statistic title="Avg Stops / Route" value={stats.routes.avgStopsPerRoute} precision={1} />
                  </Card>
                </Col>
                <Col xs={12} sm={6}>
                  <Card className="stat-card">
                    <Statistic title="Total Distance" value={stats.routes.totalDistanceKm} suffix="km" precision={1} />
                  </Card>
                </Col>
                {/* OVO-130 — admin view shows BOTH durations: shift (bike-out
                    → bike-in, payroll) and client service time (first pickup →
                    last delivery, billing). Replaces the meaningless optimizer
                    "working time". */}
                {/* OVO-248 — the time KPIs are clickable: an aggregate you
                    can't decompose is one you can't trust. Opens the routes
                    that sum into it, worst first. */}
                <Col xs={12} sm={6}>
                  <Card
                    className="stat-card stat-card-clickable"
                    onClick={() => setDrilldown({ metric: 'shift', title: 'Total Shift Time — contributing routes' })}
                  >
                    <Tooltip title={DRILLDOWN_HINT}>
                      <Statistic
                        title="Total Shift Time"
                        value={formatMins(stats.routes.totalShiftMins ?? 0)}
                        prefix={<FieldTimeOutlined />}
                        formatter={(val) => val}
                      />
                    </Tooltip>
                  </Card>
                </Col>
                <Col xs={12} sm={6}>
                  <Card className="stat-card">
                    <Statistic
                      title="Avg Shift / Route"
                      value={formatMins(stats.routes.avgShiftMins ?? 0)}
                      prefix={<FieldTimeOutlined />}
                      formatter={(val) => val}
                    />
                  </Card>
                </Col>
                {/* Start punctuality — global: % of routes whose "Start route"
                    badge is within 10' of the planned shift start. A late
                    badge and a late start read the same, by design (the stat
                    exists to catch drivers not using the app at the moment). */}
                {stats.punctuality && (
                  <Col xs={12} sm={6}>
                    <Card className="stat-card">
                      <Tooltip title={t('onTimeStartTooltip')}>
                        <Statistic
                          title={`⏰ ${t('onTimeStartKpi')}`}
                          value={
                            stats.punctuality.onTimeStartRate == null ? '—' : `${stats.punctuality.onTimeStartRate}%`
                          }
                          formatter={(val) => val}
                          valueStyle={{
                            color:
                              stats.punctuality.onTimeStartRate == null
                                ? undefined
                                : stats.punctuality.onTimeStartRate >= 80
                                  ? '#2e7d32'
                                  : stats.punctuality.onTimeStartRate >= 50
                                    ? '#f9a825'
                                    : '#c62828',
                          }}
                        />
                      </Tooltip>
                    </Card>
                  </Col>
                )}
                {/* OVO-239 — the two rate metrics Felipe asked for. Tooltips
                    name the denominators, because a rate whose divisor is
                    unclear is exactly as untrustworthy as an aggregate you
                    can't decompose. */}
                <Col xs={12} sm={6}>
                  <Card className="stat-card">
                    <Tooltip title="Distance ÷ shift hours (bike-out → bike-in). Shifts longer than 18h are excluded as never-closed.">
                      <Statistic
                        title={t('avgSpeedKpi')}
                        value={stats.routes.avgSpeedKmh == null ? '—' : `${stats.routes.avgSpeedKmh} km/h`}
                        prefix={<CarOutlined />}
                        formatter={(val) => val}
                      />
                    </Tooltip>
                  </Card>
                </Col>
                <Col xs={12} sm={6}>
                  <Card className="stat-card">
                    <Tooltip title="Stops ÷ client-service hours (first pickup → last delivery), so depot time doesn't dilute it.">
                      <Statistic
                        title={t('stopsPerHourKpi')}
                        value={stats.routes.stopsPerHour ?? '—'}
                        prefix={<InboxOutlined />}
                        formatter={(val) => val}
                      />
                    </Tooltip>
                  </Card>
                </Col>
                {/* OVO-247 — these two follow the partner filter: with partners
                    selected they show only those partners' stops on each route,
                    matching that partner's own Stats page. Spelled out in the
                    tooltip because "service time" was previously read as the
                    driver's whole-route span and looked unresponsive. */}
                <Col xs={12} sm={6}>
                  <Card
                    className="stat-card stat-card-clickable"
                    onClick={() =>
                      setDrilldown({ metric: 'clientService', title: 'Client Service Time — contributing routes' })
                    }
                  >
                    <Tooltip title={`${CLIENT_SERVICE_HINT} ${DRILLDOWN_HINT}`}>
                      <Statistic
                        title="Total Client Service Time"
                        value={formatMins(stats.routes.totalClientServiceMins ?? 0)}
                        prefix={<FieldTimeOutlined />}
                        formatter={(val) => val}
                      />
                    </Tooltip>
                  </Card>
                </Col>
                <Col xs={12} sm={6}>
                  <Card className="stat-card">
                    <Tooltip title={CLIENT_SERVICE_HINT}>
                      <Statistic
                        title="Avg Client Service / Route"
                        value={formatMins(stats.routes.avgClientServiceMins ?? stats.routes.avgDurationMins)}
                        prefix={<FieldTimeOutlined />}
                        formatter={(val) => val}
                      />
                    </Tooltip>
                  </Card>
                </Col>
                <Col xs={12} sm={6}>
                  <Card className="stat-card">
                    <Statistic
                      title="Total Prise de service"
                      value={formatMins(stats.routes.totalPriseDeServiceMins ?? 0)}
                      prefix={<FieldTimeOutlined />}
                      formatter={(val) => val}
                    />
                  </Card>
                </Col>
                <Col xs={12} sm={6}>
                  <Card className="stat-card">
                    <Statistic
                      title="Avg Prise de service / Route"
                      value={formatMins(stats.routes.avgPriseDeServiceMins ?? 0)}
                      prefix={<FieldTimeOutlined />}
                      formatter={(val) => val}
                    />
                  </Card>
                </Col>
                {/* OVO-106 — global CO2 totals */}
                {stats.routes.totalCo2Kg != null && (
                  <>
                    <Col xs={12} sm={6}>
                      <Card className="stat-card">
                        <Statistic
                          title="Total CO₂"
                          value={stats.routes.totalCo2Kg}
                          suffix="kg"
                          precision={2}
                          valueStyle={{ color: '#2e7d32' }}
                        />
                      </Card>
                    </Col>
                    <Col xs={12} sm={6}>
                      <Card className="stat-card">
                        <Statistic
                          title="Avg CO₂ / Route"
                          value={stats.routes.avgCo2KgPerRoute}
                          suffix="kg"
                          precision={2}
                        />
                      </Card>
                    </Col>
                    {stats.routes.totalCo2SavedKg > 0 && (
                      <Col xs={12} sm={6}>
                        <Tooltip
                          title={`Compared to a reference vehicle at ${stats.routes.oilBaselineGPerKm} g CO₂-eq/km (editable; default: diesel car, Mobitool v3.1)`}
                        >
                          <Card className="stat-card stat-card-co2-saved">
                            <Statistic
                              title={
                                <span style={{ color: '#2e7d32', fontWeight: 600 }}>
                                  <span role="img" aria-label="eco" style={{ marginRight: 6 }}>
                                    🌱
                                  </span>
                                  CO₂ saved vs car
                                </span>
                              }
                              value={stats.routes.totalCo2SavedKg}
                              suffix="kg"
                              precision={2}
                              prefix={
                                <span role="img" aria-label="leaf" style={{ color: '#2e7d32', marginRight: 4 }}>
                                  🌿
                                </span>
                              }
                              valueStyle={{ color: '#2e7d32', fontWeight: 700 }}
                            />
                          </Card>
                        </Tooltip>
                      </Col>
                    )}
                  </>
                )}
              </Row>
            </div>

            {/* Charts Row 2 */}
            <Row gutter={[16, 16]} className="stats-section">
              <Col xs={24} md={12}>
                <Card className="chart-card" title={cardTitle('Items by Category')}>
                  {categoryData.length > 0 ? (
                    <Pie
                      data={categoryData}
                      angleField="value"
                      colorField="type"
                      color={categoryData.map((d) => d.color)}
                      {...pieConfig}
                    />
                  ) : (
                    <Empty description="No items collected" />
                  )}
                </Card>
              </Col>
              <Col xs={24} md={12}>
                <Card className="chart-card" title={cardTitle('Orders by Day of Week')}>
                  <Column
                    data={dowData}
                    xField="day"
                    yField="orders"
                    height={240}
                    color="#212121"
                    label={{ position: 'top', style: { fontWeight: 600 } }}
                    xAxis={{ label: { style: { fontSize: 12 } } }}
                  />
                </Card>
              </Col>
            </Row>

            {/* Collection & Time */}
            <div className="stats-section">
              {sectionTitle('Collection & Time')}
              <Row gutter={[16, 16]}>
                <Col xs={12} sm={6}>
                  <Card className="stat-card">
                    <Statistic title="Items Collected" value={stats.collection.totalItems} prefix={<InboxOutlined />} />
                  </Card>
                </Col>
                <Col xs={12} sm={6}>
                  <Card className="stat-card">
                    <Statistic title="Items / Route" value={stats.collection.avgItemsPerRoute} precision={1} />
                  </Card>
                </Col>
                <Col xs={12} sm={6}>
                  <Card className="stat-card">
                    <Statistic
                      title={
                        <Tooltip title="Average time per task — from the previous stop's completion to this stop's completion (approach travel + time on site). Uses actual badged times, falling back to the planned ETA when a stop wasn't logged.">
                          Avg time / task
                        </Tooltip>
                      }
                      value={formatMins(stats.time.avgTimePerStopMins)}
                      formatter={(val) => val}
                    />
                  </Card>
                </Col>
                <Col xs={12} sm={6}>
                  <Card className="stat-card">
                    <Statistic
                      title="Return Drop-off"
                      value={stats.collection.returnDropoffRate}
                      suffix="%"
                      precision={1}
                    />
                  </Card>
                </Col>
              </Row>
            </div>

            {/* Partner Comparison Table — hidden in a single-tenant instance (OVO-132) */}
            {!tenantConfig.isSingleTenant && stats.byPartner && stats.byPartner.length > 0 && (
              <div className="stats-section">
                {sectionTitle('Partner Comparison')}
                <Card className="partner-table-card">
                  <Table
                    dataSource={stats.byPartner}
                    columns={partnerColumns}
                    rowKey="partnerId"
                    pagination={false}
                    size="small"
                    scroll={{ x: 'max-content' }}
                  />
                </Card>
              </div>
            )}

            {/* OVO-239 — Driver & Vehicle comparison, the counterpart to the
                Partner table above. OVO-221 replaced the old breakdowns with
                filters; these come back as COMPARISON tables carrying the new
                rate metrics, so drivers/vehicles can be read side by side
                instead of in two browser tabs. ADMIN-ONLY: driver identity must
                never reach a partner view (OVO-002), which is why this lives
                here and not on /partners/me/stats. */}
            {stats.byDriver && stats.byDriver.length > 0 && (
              <div className="stats-section">
                {sectionTitle(t('driverComparison'))}
                <Card className="partner-table-card">
                  <Table
                    dataSource={stats.byDriver}
                    columns={driverComparisonColumns}
                    rowKey="driverId"
                    pagination={false}
                    size="small"
                    scroll={{ x: 'max-content' }}
                  />
                </Card>
              </div>
            )}

            {stats.byVehicleType && stats.byVehicleType.length > 0 && (
              <div className="stats-section">
                {sectionTitle(t('vehicleComparison'))}
                <Card className="partner-table-card">
                  <Table
                    dataSource={stats.byVehicleType}
                    columns={vehicleComparisonColumns}
                    rowKey="vehicleType"
                    pagination={false}
                    size="small"
                    scroll={{ x: 'max-content' }}
                  />
                </Card>
              </div>
            )}

            {/* OVO-111 — visits by client across the global scope */}
            <div className="stats-section">
              <StatsByName data={stats.byClient} loading={loading} titleSuffix={rangeText} />
            </div>

            {/* CO₂ comparison baseline editor — Felipe Jun 2026. The factor the
                "saved vs car" figures compare against (diesel car ~140, diesel
                van ~250-300). Persisted globally; applies to every CO₂ saved
                figure (stats, route detail, partner stats, CSV). */}
            <div className="stats-section">
              <Card size="small" className="co2-baseline-card">
                <Space wrap align="center" size="small">
                  <span style={{ fontWeight: 600 }}>
                    <span role="img" aria-label="leaf" style={{ marginRight: 6 }}>
                      🌿
                    </span>
                    {t('co2BaselineLabel')}
                  </span>
                  <InputNumber
                    min={1}
                    max={2000}
                    value={baselineInput}
                    onChange={setBaselineInput}
                    addonAfter="g CO₂/km"
                    style={{ width: 170 }}
                  />
                  <Button size="small" onClick={() => setBaselineInput(140)}>
                    {t('co2PresetCar')}
                  </Button>
                  <Button size="small" onClick={() => setBaselineInput(250)}>
                    {t('co2PresetVan')}
                  </Button>
                  <Button
                    type="primary"
                    size="small"
                    loading={savingBaseline}
                    disabled={!baselineInput || baselineInput <= 0}
                    onClick={handleSaveBaseline}
                  >
                    {t('co2BaselineSave')}
                  </Button>
                  {baselineSaved && <span style={{ color: '#2e7d32', fontWeight: 600 }}>{t('co2BaselineSaved')}</span>}
                  <span style={{ color: '#8c8c8c', fontSize: 12 }}>{t('co2BaselineHint')}</span>
                </Space>
              </Card>
            </div>

            {/* OVO-106 — methodology + reference link */}
            {stats.routes.totalCo2Kg != null && (
              <div className="stats-section" style={{ fontSize: 12, color: '#8c8c8c', paddingTop: 8 }}>
                CO₂ estimates use per-vehicle-km emission factors from{' '}
                <a
                  href="https://www.suisseenergie.ch/programmes/calculateur-environnemental-transport/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  SuisseEnergie's transport environmental calculator
                </a>{' '}
                (Mobitool v3.1). Savings are computed against a configurable reference vehicle (
                {stats.routes.oilBaselineGPerKm} g CO₂-eq/km — default: average diesel passenger car).
              </div>
            )}
          </Spin>
        )}
      </div>

      {/* OVO-248 — the routes behind the clicked KPI, using the page's own
          filters so the rows provably sum to the card. */}
      <StatsRoutesDrilldown
        open={!!drilldown}
        metric={drilldown?.metric}
        title={drilldown?.title}
        query={{
          query: { period: filter.period, from: filter.from, to: filter.to },
          partnerIds: selectedPartnerIds,
          fleetTags: selectedVehicleTypes,
          driverIds: selectedDriverIds,
        }}
        onClose={() => setDrilldown(null)}
      />
    </div>
  );
};

export default GlobalStats;
